import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import { useToastStore } from '../stores/toastStore';
import { useNavigationContextStore } from '../stores/navigationContextStore';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Chip } from 'primereact/chip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tooltip } from 'primereact/tooltip';
import Citation from './Citation/Citation';
import { SelectButton } from 'primereact/selectbutton';
// NOTE: Skip global Chat.scss during widget build; the widget injects styles into its shadow root,
// and loading it globally would leak PrimeReact layer overrides into host apps (e.g., PrimeNG).
if (import.meta.env.MODE !== 'widget') {
  import("./Chat.scss");
}
import { VegaEmbed } from "react-vega";
import AssistantIcon from '../assets/images/AssistantIcon';
import CoachIcon from '../assets/images/CoachIcon';
import SendIcon from '../assets/images/SendIcon';
import UploadIcon from '../assets/images/UploadIcon';
import ThumbsDownIcon from '../assets/images/ThumbsDownIcon';
import ThumbsUpIcon from '../assets/images/ThumbsUpIcon';
import ChevronDown from '../assets/images/ChevronDown';
import ChevronUpIcon from '../assets/images/ChevronUp';
import Cancel from '../assets/images/Cancel';
import Plus from '../assets/images/PlusIcon';
import History from '../assets/images/HistoryIcon';
import CustomButton from './Common/CustomButton/CustomButton';
import { useCustomDashboardStore } from '../stores/customDashboardStore';
import type { FeedbackOptions } from '../api/chat';

type ChatProps = {
  onFeedback?: (payload: any) => void;
};

export const Chat = ({ onFeedback }: ChatProps) => {
  const { negotiation_id } = useParams<{ negotiation_id?: string }>();
  // Force negotiation_id to a number once and reuse throughout
  const negotiationId = negotiation_id ? parseInt(negotiation_id, 10) : undefined;
  const token = useAuthStore((state) => state.token);
  const { getAgentType, selectedTenderId, selectedSupplierId, analysisPageName } = useNavigationContextStore();
  const { saveWidget } = useCustomDashboardStore();
  const { 
    isOpen, 
    isHistoryPanelOpen,
    closeChat, 
    toggleHistoryPanel,
    messages, 
    inputValue, 
    setInputValue, 
    handleChange,
    sendMessage, 
    isLoading, 
    submitFeedback,
    createNewChat,
    loadThread,
    loadThreads,
    threads,
    currentThreadId,
    negotiationId: storeNegotiationId,
    agentType: storeAgentType,
    supplierId: storeSupplierId,
    tenderId: storeTenderId
  } = useChatStore();
  const { info } = useToastStore();
  const chatRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadStatus, setUploadStatus] = useState<{ loading: boolean, error: string | null }>({ loading: false, error: null });
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [messageFeedback, setMessageFeedback] = useState<Record<string, 'up' | 'down' | null>>({});
  const [currentFeedbackMessageId, setCurrentFeedbackMessageId] = useState<string | null>(null);
  const [feedbackOptions, setFeedbackOptions] = useState<FeedbackOptions>({
    factsWithoutNarrative: false,
    weakCommercialLink: false,
    risksNotAcknowledged: false,
    incorrectSource: false,
    incorrectAnswer: false
  });

  const [openReasoning, setOpenReasoning] = useState<Record<string, boolean>>({});
  const [openReference, setOpenReference] = useState<Record<string, boolean>>({});
  const [expandedToolCalls, setExpandedToolCalls] = useState<Record<string, boolean>>({});
  const [selectedReference, setSelectedReference] = useState<any>(null);
  const [coachingButton, setCoachingButton] = useState('assistant');
  const [suggestionsOpen, setSuggestionsOpen] = useState(true);
  const [pendingGraph, setPendingGraph] = useState<null | {
    pageName: string;
    negotiationId?: number;
    chartSpec: any;
    messageId: string;
  }>(null);
  const promptedCharts = useRef<Set<string>>(new Set());
  const autoSaveNextChart = useRef(false);

  const emitFeedback = useCallback((payload: any) => {
    if (!onFeedback) return;
    onFeedback({
      ...payload,
      timestamp: new Date().toISOString(),
    });
  }, [onFeedback]);
  
  const options = [
    { label: 'AI Assistant', value: 'assistant', icon: <AssistantIcon /> },
    { label: 'Coaching Assistant', value: 'coach', icon: <CoachIcon /> }
  ];
  // Reset feedback modal when chat opens/closes to prevent auto-showing
  useEffect(() => {
    if (isOpen && token) {
      setFeedbackModal(false);
      setFeedbackText('');
      // Load threads when chat opens
      loadThreads();
      // If no messages and no current thread, show blank thread
      if (messages.length === 0 && !currentThreadId) {
        createNewChat();
      }
    }

    return () => {
        setOpenReasoning({});
        setOpenReference({});
        setExpandedToolCalls({});
        setSelectedReference(null);
    }
  }, [isOpen, token, loadThreads, createNewChat, messages.length, currentThreadId]);

  const saveGraphAsWidget = async (payload: { pageName: string; negotiationId?: number; chartSpec: any; messageId: string }) => {
    if (!payload.negotiationId) return;
    try {
      // Ensure user/session is available before hitting protected endpoint
      // if (!token) {
      //   await useAuthStore.getState().getUserIdentity();
      // }
      await saveWidget({
        negotiationId: payload.negotiationId,
        pageName: payload.pageName,
        componentType: "Graph",
        chartVegajson: payload.chartSpec,
        chartDescription: payload.chartSpec.title,
      });
      const { addMessage } = useChatStore.getState();
      addMessage({
        text: `Graph added to “${payload.pageName}”.`,
        sender: 'ai',
      });
    } catch (err: any) {
      if (err?.message?.includes('401')) {
        useToastStore.getState().error?.('Unauthorized', 'Please sign in again to save widgets.');
      }
      const { addMessage } = useChatStore.getState();
      addMessage({
        text: `Failed to save graph: ${err?.message || 'Unknown error'}`,
        sender: 'ai',
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedMessage = inputValue.trim();
    if (trimmedMessage && !isLoading) {
      if (pendingGraph && /^y(es)?$/i.test(trimmedMessage)) {
        // Record the user's yes in the chat history
        useChatStore.getState().addMessage({ text: trimmedMessage, sender: 'user' });
        saveGraphAsWidget(pendingGraph);
        setPendingGraph(null);
        autoSaveNextChart.current = false;
        setInputValue('');
        return;
      } else if (pendingGraph && shouldAutoSaveToCustomTab(trimmedMessage)) {
        // Allow explicit save request after the chart is shown
        useChatStore.getState().addMessage({ text: trimmedMessage, sender: 'user' });
        saveGraphAsWidget(pendingGraph);
        setPendingGraph(null);
        autoSaveNextChart.current = false;
        setInputValue('');
        return;
      } else if (pendingGraph) {
        setPendingGraph(null);
      }
      autoSaveNextChart.current = shouldAutoSaveToCustomTab(trimmedMessage);
      const effectiveNegotiationId = negotiationId ?? storeNegotiationId;
      const effectiveSupplierId = selectedSupplierId ?? storeSupplierId;
      const effectiveTenderId = selectedTenderId ?? storeTenderId;
      let agentType = getAgentType();
      // If coachingButton is 'coach', set agentType to 'negotiation_coach'
      if(coachingButton === 'coach') {
        agentType = 'negotiation_coach';
      }
      if (!agentType && storeAgentType) {
        agentType = storeAgentType;
      }
      sendMessage(
        trimmedMessage, 
        effectiveNegotiationId, 
        agentType || storeAgentType, 
        effectiveSupplierId ?? undefined, 
        effectiveTenderId ?? undefined
      );
      setInputValue('');
    } 
  };

  const handleFileUpload = () => {
    // Trigger the hidden file input click
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const effectiveNegotiationId = negotiationId ?? storeNegotiationId;

    // Check if we have a negotiation_id
    if (effectiveNegotiationId === undefined) {
      setUploadStatus({ loading: false, error: 'File upload is only available within a negotiation context' });
      return;
    }

    const file = files[0];
    setUploadStatus({ loading: true, error: null });

    try {
      await handleChange(file, effectiveNegotiationId);
      setUploadStatus({ loading: false, error: null });
    } catch (error) {
      console.error('File upload error:', error);
      setUploadStatus({ loading: false, error: error instanceof Error ? error.message : 'Upload failed' });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFeedback = async (messageId: string, type: 'up' | 'down') => {
    
    setMessageFeedback(prev => ({ ...prev, [messageId]: type }));
    
    if (type === 'up') {
      // Submit positive feedback immediately
      try {
        const response = await submitFeedback({
          messageId,
          feedbackType: 'up'
        });
        emitFeedback(response);
        info('Feedback Received', 'Thank you for your feedback and helping us improve the NSP. Please note, that in the prototype test phase your comments as well as your chat history can be used for bug analysis.');
      } catch (error) {
        console.error('Failed to submit positive feedback:', error);
        emitFeedback(error);
        // Reset the feedback state on error
        setMessageFeedback(prev => ({ ...prev, [messageId]: null }));
      }
    } else {
      // Store message ID and show modal for negative feedback
      setCurrentFeedbackMessageId(messageId);
      setFeedbackModal(true);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!currentFeedbackMessageId) return;
    
    try {
      
      const trimmedFeedback = feedbackText.trim();
      const response = await submitFeedback({
        messageId: currentFeedbackMessageId,
        feedbackType: 'down',
        feedbackText: trimmedFeedback || undefined,
        feedbackOptions
      });
      emitFeedback(response);
      info('Feedback Received', 'Thank you for your feedback and helping us improve the NSP. Please note, that in the prototype test phase your comments as well as your chat history can be used for bug analysis.');
      setFeedbackModal(false);
      setCurrentFeedbackMessageId(null);
      setFeedbackText('');
      setFeedbackOptions({
        factsWithoutNarrative: false,
        weakCommercialLink: false,
        risksNotAcknowledged: false,
        incorrectSource: false,
        incorrectAnswer: false
      });
    } catch (error) {
      console.error('Failed to submit negative feedback:', error);
      // const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      emitFeedback(error);
      // Reset the feedback state on error
      if (currentFeedbackMessageId) {
        setMessageFeedback(prev => ({ ...prev, [currentFeedbackMessageId]: null }));
      }
    }
  };

  const handleFeedbackCancel = () => {
    setFeedbackModal(false);
    setCurrentFeedbackMessageId(null);
    setFeedbackText('');
    setFeedbackOptions({
      factsWithoutNarrative: false,
      weakCommercialLink: false,
      risksNotAcknowledged: false,
      incorrectSource: false,
      incorrectAnswer: false
    });
  };

  const handleCheckboxChange = (option: keyof typeof feedbackOptions) => {
    setFeedbackOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleSuggestedQuestionClick = (question: string) => {
    if (!isLoading) {
      const effectiveNegotiationId = negotiationId ?? storeNegotiationId;
      const effectiveSupplierId = selectedSupplierId ?? storeSupplierId;
      const effectiveTenderId = selectedTenderId ?? storeTenderId;
      let agentType = getAgentType();
      // If coachingButton is 'coach', set agentType to 'negotiation_coach'
      if(coachingButton === 'coach') {
        agentType = 'negotiation_coach';
      }
      if (!agentType && storeAgentType) {
        agentType = storeAgentType;
      }
      sendMessage(
        question, 
        effectiveNegotiationId, 
        agentType || storeAgentType, 
        effectiveSupplierId ?? undefined, 
        effectiveTenderId ?? undefined
      );
    }
  };

  // Get suggested questions from the latest AI message
  const getSuggestedQuestions = () => {
    const lastAiMessage = [...messages].reverse().find(msg => msg.sender === 'ai' && msg.suggestedQuestions);
    return lastAiMessage?.suggestedQuestions || [];
  };

  const buildGraphPayloadFromMessage = (message: any) => {
    if (!analysisPageName || !message?.chartReference) return null;
    const effectiveNegotiationId = negotiationId ?? storeNegotiationId;
    return {
      pageName: analysisPageName,
      negotiationId: effectiveNegotiationId,
      chartSpec: {
        ...(message.chartReference || {}),
        data: {
          name: (message.chartReference as any)?.data?.name || 'dataset',
          values:
            (message.chartReference as any)?.data?.values ||
            (message.chartReference as any)?.values ||
            [],
        },
      },
      messageId: message.id,
    };
  };

  const shouldAutoSaveToCustomTab = (text: string) => {
    if (!analysisPageName) return false;
    const normalized = text.toLowerCase();
    const mentionsCustomTab = normalized.includes('custom tab') || normalized.includes('custom dashboard');
    const mentionsSaveVerb =
      normalized.includes('save') ||
      normalized.includes('add') ||
      normalized.includes('store') ||
      normalized.includes('keep') ||
      normalized.includes('put');
    return mentionsCustomTab && mentionsSaveVerb;
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Prompt to save graph when on a custom analysis page and an AI message includes a chart
  useEffect(() => {
    if (!analysisPageName) return;
    const lastAiWithChart = [...messages]
      .reverse()
      .find((m) => m.sender === 'ai' && m.chartReference && !promptedCharts.current.has(m.id));
    if (lastAiWithChart) {
      promptedCharts.current.add(lastAiWithChart.id);
      const nextGraph = {
        pageName: analysisPageName,
        negotiationId: negotiationId ?? storeNegotiationId,
        chartSpec: {
          ...(lastAiWithChart.chartReference || {}),
          data: {
            name:
              (lastAiWithChart.chartReference as any)?.data?.name ||
              'dataset',
            values:
              (lastAiWithChart.chartReference as any)?.data?.values ||
              (lastAiWithChart.chartReference as any)?.values ||
              [],
          },
        },
        messageId: lastAiWithChart.id,
      };
      if (autoSaveNextChart.current) {
        autoSaveNextChart.current = false;
        setPendingGraph(null);
        saveGraphAsWidget(nextGraph);
        return;
      }
      setPendingGraph(nextGraph);
    }
  }, [messages, analysisPageName, negotiationId, storeNegotiationId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const path = event.composedPath ? event.composedPath() : [];
      const target = event.target as Element;
      const clickedInsideChat = path.includes(chatRef.current as EventTarget);
      const clickedChatHost = path.some(
        (el) => el instanceof HTMLElement && el.tagName === 'REACT-CHAT-WIDGET'
      );
      const clickedToggleButton = path.some(
        (el) => el instanceof HTMLElement && el.dataset?.rcwToggle === 'true'
      );
      const clickedDialog = path.some(
        (el) =>
          el instanceof HTMLElement &&
          (el.classList.contains('p-dialog') || el.classList.contains('p-dialog-mask'))
      );

      // Don't close if clicking inside the shadow DOM, on the host, toggle button, or modal
      if (
        chatRef.current &&
        !clickedInsideChat &&
        !clickedChatHost &&
        !clickedToggleButton &&
        !clickedDialog &&
        !target.closest('button[class*=\"fixed right-4\"]')
      ) {
        closeChat();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeChat]);

  const toggleReasoning = (id:any) => {
    setOpenReasoning((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleReference = (messageId: string, referenceId?: string) => {

    // If closing the panel
    if (openReference[messageId] && !referenceId) {
      setOpenReference((prev) => ({
        ...prev,
        [messageId]: false,
      }));
      setSelectedReference(null);
      return;
    }

    // If opening the panel or switching references
    setOpenReference((prev) => ({
      ...prev,
      [messageId]: true,
    }));

    // Find and set the selected reference
    if (referenceId) {
      const message = messages.find(msg => msg.id === messageId);
      const reference = message?.references?.find(ref => ref.reference_id === referenceId);
      if (reference) {
        setSelectedReference(reference);
      }
    }
  };

  const toggleToolCall = (messageId: string, stepIdx: number) => {
    const key = `${messageId}-${stepIdx}`;
    setExpandedToolCalls((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getReferenceIcon = (referenceType: string) => {
    switch (referenceType) {
      case 'table': return 'pi-table';
      case 'document': return 'pi-file-pdf';
      case 'image': return 'pi-image';
      default: return 'pi-info-circle';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={chatRef}
      className={`
        fixed inset-4 sm:inset-auto z-1002 flex flex-col overflow-hidden
        transition-all duration-300 ease-in-out
        ${Object.values(openReference).length > 0 && Object.values(openReference).includes(true)
          ? "sm:w-[500px] sm:h-[85vh] md:w-[640px] md:h-[85vh] lg:w-[85%] lg:h-[85vh]"
          : "sm:w-[400px] sm:h-[85vh] md:w-[540px] md:h-[85vh] lg:w-[720px] lg:h-[85vh]"
        }
      `}
      style={{
        zIndex: 1002,
        bottom: '75px',
        right: '80px',
        backgroundColor: 'var(--surface-0)',
        borderRadius: 'var(--border-radius-xl)',
        boxShadow: 'var(--overlay-modal-shadow)',
        border: '1px solid var(--content-border-color)'
      }}
    >
      <div className="relative flex flex-col h-full">
      <div className="flex items-center justify-between flex-shrink-0" style={{
        padding: 'var(--padding-md)',
        borderBottom: '1px solid var(--content-border-color)',
        background: coachingButton === "assistant" ? 'var(--color-blue-500)' : 'var(--color-amber-700)',
        color: 'var(--surface-default)'
      }}>
        <h3 style={{
          fontSize: 'var(--typography-heading-lg-font-size)',
          fontWeight: 'var(--font-weight-700)',
          margin: 0
        }}>{ options.map((ele) => ele.value === coachingButton ? ele.label : "") }</h3>
        <div className="flex items-center" style={{ gap: 'var(--gap-sm)' }}>
          <Tooltip target=".new-chat-icon" />
          <button
              type="button"
              onClick={createNewChat}
              className="transition-colors new-chat-icon"
              disabled={isLoading || uploadStatus.loading}
              style={{
                color: isLoading || uploadStatus.loading ? 'var(--form-field-disabled-color)' : 'var(--surface-default)',
                background: 'none',
                border: 'none',
                cursor: isLoading || uploadStatus.loading ? 'not-allowed' : 'pointer',
                padding: 'var(--padding-xs)'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && !uploadStatus.loading) {
                  e.currentTarget.style.color = 'var(--text-hover-color)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && !uploadStatus.loading) {
                  e.currentTarget.style.color = 'var(--surface-default)';
                }
              }}
              data-pr-tooltip="Start new chat"
						
            >
              <Plus />
          </button>
          {threads.length > 0 && (
            <>
              <Tooltip target=".history-icon" />
              <button
                onClick={toggleHistoryPanel}
                className="transition-colors history-icon"
                type="button"
                style={{
                  color: 'var(--surface-default)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 'var(--padding-xs)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-hover-color)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--surface-default)'}
                data-pr-tooltip="Chat History"
              >
                <History />
              </button>
            </>
          )}
          <button
            onClick={closeChat}
            className="transition-colors"
            type="button"
            style={{
              color: 'var(--surface-default)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-hover-color)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--surface-default)'}
          >
            {/* <i className="pi pi-times" style={{ fontSize: 'var(--icon-size-md)' }}></i> */}
            <Cancel />
          </button>
        </div>
      </div>
      
      
      <div className="flex-1 space-y-4 content-area overflow-y-auto flex justify-between">

         {/* Citations */}
        { messages.length > 0 && messages.some(msg => openReference[msg.id]) &&
        <div
          className="citations overflow-y-auto"
          style={{
            padding: 'var(--padding-sm)',
            background: 'var(--border-muted)',
            width: '50%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Citation reference={selectedReference} />
        </div>}
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto space-y-4" style={{ padding: 'var(--padding-md)' }}>
          {pendingGraph && (
            <div
              style={{
                border: '1px solid var(--content-border-color)',
                borderRadius: 'var(--border-radius-lg)',
                padding: '12px 14px',
                background: 'var(--surface-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ fontSize: '0.95rem', color: 'var(--text-color)' }}>
                Chart ready to save to “{pendingGraph.pageName}”.
              </div>
              <div className="flex items-center" style={{ gap: '8px' }}>
                <CustomButton
                  iconsOnly={false}
                  variant="primary"
                  label="Save to tab"
                  onClick={() => {
                    saveGraphAsWidget(pendingGraph);
                    setPendingGraph(null);
                    autoSaveNextChart.current = false;
                  }}
                />
                <CustomButton
                  iconsOnly={false}
                  variant="secondary"
                  label="Dismiss"
                  onClick={() => {
                    setPendingGraph(null);
                    autoSaveNextChart.current = false;
                  }}
                />
              </div>
            </div>
          )}
          {messages.map((message) => {
            // Check if this is the last AI message and we're currently loading
            const isLastAiMessage = message.sender === 'ai' &&
                                     messages.indexOf(message) === messages.length - 1 &&
                                     isLoading;

            // Don't render empty AI messages unless they're the current streaming message
            if (message.sender === 'ai' &&
                !message.text &&
                (!message.reasoningSteps || message.reasoningSteps.length === 0) &&
                !message.chartReference &&
                !isLastAiMessage) {
              return null;
            }

            return (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className="flex flex-col max-w-lg lg:max-w-xl">
                  <div
                    style={{
                      padding: 'var(--padding-md)',
                      borderRadius: 'var(--border-radius-lg)',
                      backgroundColor: message.sender === 'user'
                        ? coachingButton === "assistant" ? 'var(--text-muted)' : 'var(--color-amber-700)'
                        : message.type === 'reasoning'
                          ? 'var(--surface-50)'
                          : 'var(--surface-100)',
                      color: message.sender === 'user'
                        ? 'var(--surface-default)'
                        : message.type === 'reasoning'
                          ? 'var(--text-muted-color)'
                          : 'var(--text-color)',
                      fontStyle: message.type === 'reasoning' ? 'italic' : 'normal',
                      opacity: message.type === 'reasoning' ? 0.8 : 1
                    }}
                  >
                  {message.sender === 'ai' ? (
                    <div style={{
                      fontSize: 'var(--typography-body-font-size)',
                      margin: 0,
                      lineHeight: 'var(--typography-body-line-height)',
                      color: message.type === 'reasoning' ? 'var(--text-muted-color)' : 'var(--text-color)'
                    }}>
                    {message.type === 'reasoning' && (
                      <div style={{
                        fontWeight: 'var(--font-weight-600)',
                        marginBottom: '0.25rem',
                        fontSize: 'var(--font-size-xs)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Reasoning:
                      </div>
                    )}
                    {message?.chartReference && (
                      <div
                        style={{
                          marginTop: '0.5rem',
                          marginBottom: '0.5rem',
                          padding: '12px',
                          borderRadius: '10px',
                          background: 'var(--surface-default)',
                          border: '1px solid var(--content-border-color)'
                        }}
                      >
                        <VegaEmbed
                        // @ts-ignore
                          spec={{
                            ...(message.chartReference || {}),
                            data: {
                              name:
                                (message.chartReference as any)?.data?.name ||
                                'dataset',
                              values:
                                (message.chartReference as any)?.data?.values ||
                                (message.chartReference as any)?.values ||
                                [],
                            },
                          }}
                          options={{
                              actions: {
                                export: true,   // no PNG/SVG download
                                source: true,   // no show Vega-Lite spec
                                compiled: false, // no show compiled Vega spec
                                editor: false    // no open in vega editor
                              }
                            }}
                        />
                        {analysisPageName && (
                          <div
                            style={{
                              marginTop: '0.5rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flexWrap: 'wrap',
                            }}
                          >
                            {(() => {
                              const payload =
                                (pendingGraph?.messageId === message.id && pendingGraph) ||
                                buildGraphPayloadFromMessage(message);
                              if (!payload) return null;
                              return (
                                <>
                                  <CustomButton
                                    iconsOnly={false}
                                    variant="primary"
                                    label="Save to tab"
                                    onClick={() => {
                                      saveGraphAsWidget(payload);
                                      if (pendingGraph?.messageId === message.id) {
                                        setPendingGraph(null);
                                      }
                                      autoSaveNextChart.current = false;
                                    }}
                                  />
                                  <CustomButton
                                    iconsOnly={false}
                                    variant="secondary"
                                    label="Dismiss"
                                    onClick={() => {
                                      if (pendingGraph?.messageId === message.id) {
                                        setPendingGraph(null);
                                      }
                                      autoSaveNextChart.current = false;
                                    }}
                                  />
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show typing indicator if this is the last AI message and we're loading with no text yet */}
                    {isLastAiMessage && !message.text ? (
                      <div className="flex items-center" style={{ gap: 'var(--gap-xs)' }}>
                        <div className="flex" style={{ gap: '0.25rem' }}>
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted-color)' }}></div>
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted-color)', animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--text-muted-color)', animationDelay: '0.2s' }}></div>
                        </div>
                        <span style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted-color)'
                        }}>AI is typing...</span>
                      </div>
                    ) : (
                      <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => (
                          <p style={{ 
                            fontSize: 'var(--typography-body-font-size)', 
                            margin: '0 0 0.5rem 0',
                            lineHeight: 'var(--typography-body-line-height)',
                            color: message.type === 'reasoning' ? 'var(--text-muted-color)' : 'var(--text-color)'
                          }}>
                            {children}
                          </p>
                        ),
                        code: ({ children }) => (
                          <code style={{
                            backgroundColor: 'var(--surface-200)',
                            padding: '0.125rem 0.25rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.875em',
                            fontFamily: 'monospace'
                          }}>
                            {children}
                          </code>
                        ),
                        pre: ({ children }) => (
                          <pre style={{
                            backgroundColor: 'var(--surface-200)',
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            overflow: 'auto',
                            fontSize: '0.875em',
                            fontFamily: 'monospace',
                            margin: '0.5rem 0'
                          }}>
                            {children}
                          </pre>
                        ),
                        ul: ({ children }) => (
                          <ul style={{
                            margin: '0.5rem 0',
                            paddingLeft: '1.5rem'
                          }}>
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol style={{
                            margin: '0.5rem 0',
                            paddingLeft: '1.5rem'
                          }}>
                            {children}
                          </ol>
                        ),
                        strong: ({ children }) => (
                          <strong style={{
                            fontWeight: 'var(--font-weight-700)'
                          }}>
                            {children}
                          </strong>
                        ),
                        em: ({ children }) => (
                          <em style={{
                            fontStyle: 'italic'
                          }}>
                            {children}
                          </em>
                        ),
                        table: ({ children }) => (
                          <div style={{ overflowX: 'auto', margin: '0.5rem 0' }}>
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: 'var(--typography-body-font-size)'
                            }}>
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children }) => (
                          <thead style={{ backgroundColor: 'var(--surface-200)' }}>
                            {children}
                          </thead>
                        ),
                        tbody: ({ children }) => (
                          <tbody>
                            {children}
                          </tbody>
                        ),
                        tr: ({ children }) => (
                          <tr style={{ borderBottom: '1px solid var(--content-border-color)' }}>
                            {children}
                          </tr>
                        ),
                        th: ({ children }) => (
                          <th style={{
                            textAlign: 'left',
                            padding: '0.5rem',
                            borderBottom: '2px solid var(--content-border-color)'
                          }}>
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td style={{
                            padding: '0.5rem',
                            borderBottom: '1px solid var(--content-border-color)'
                          }}>
                            {children}
                          </td>
                        )
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                    )}
                    </div>
                  ) : (
                    <p style={{
                      fontSize: 'var(--typography-body-font-size)',
                      margin: 0,
                      lineHeight: 'var(--typography-body-line-height)',
                      color: 'var(--surface-default)'
                    }}>{message.text}</p>
                  )}
                  {message.text && (
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      margin: '0.25rem 0 0 0',
                      color: message.sender === 'user'
                        ? 'rgba(255, 255, 255, 0.8)'
                        : 'var(--text-muted-color)'
                    }}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}

                </div>

                {message.sender === 'ai' && message.type !== 'reasoning' && messages.findIndex(m => m.id === message.id) > 0 && (
                  <div style={{ gap: 'var(--gap-xs)' }}>
                  {!isLoading && message.text && (
                    <div className='flex items-center justify-between w-full'>
                      <p className='my-0 note-text'>
                        NOTE: This content is AI Generated please check for accuracy
                      </p>
                      <div className="action-button">
                        <button
                        onClick={() => handleFeedback(message.id, 'up')}
                        className="transition-colors"
                        title="Thumbs up"
                        style={{
                          color: messageFeedback[message.id] === 'up'
                            ? 'var(--status-success)'
                            : 'var(--text-muted-color)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 'var(--padding-xs)'
                        }}
                        onMouseEnter={(e) => {
                          if (messageFeedback[message.id] !== 'up') {
                            e.currentTarget.style.color = 'var(--text-hover-color)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (messageFeedback[message.id] !== 'up') {
                            e.currentTarget.style.color = 'var(--text-muted-color)';
                          }
                        }}
                      >
                        <ThumbsUpIcon />
                      </button>
                      <button
                        onClick={() => handleFeedback(message.id, 'down')}
                        className="transition-colors"
                        title="Thumbs down"
                        style={{
                          color: messageFeedback[message.id] === 'down'
                            ? 'var(--status-error)'
                            : 'var(--text-muted-color)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 'var(--padding-xs)'
                        }}
                        onMouseEnter={(e) => {
                          if (messageFeedback[message.id] !== 'down') {
                            e.currentTarget.style.color = 'var(--text-hover-color)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (messageFeedback[message.id] !== 'down') {
                            e.currentTarget.style.color = 'var(--text-muted-color)';
                          }
                        }}
                      >
                        <ThumbsDownIcon />
                      </button>
                      </div>
                    </div>
                  )}
                  {/* Reasoning and Refrences Buttons with chevrons */}
                  <div className="action-button-reasoning flex gap-2">
                  <button
                    onClick={() => toggleReasoning(message.id)}
                    className="reasoning-button flex items-center gap-2"
                    title="Toggle Reasoning"
                  >
                    Reasoning
                    {openReasoning[message.id] ? <ChevronUpIcon /> : <ChevronDown />}
                  </button>



                {/* References Button */}
                {message.references && message.references.length > 0 && (
                  <button
                    key={message.id}
                    onClick={() => toggleReference(message.id)}
                    className="reasoning-button flex items-center gap-2"
                    title="Toggle References"
                  >
                    References ({message.references.length})
                    {openReference[message.id] ? <ChevronUpIcon /> : <ChevronDown />}
                  </button>
                )}
                  </div>
                  {openReasoning[message.id] && (
                    <div
                      style={{
                        marginTop: "4px",
                        padding: "8px 12px",
                        backgroundColor: "var(--surface-50)",
                        borderRadius: "var(--border-radius-md)",
                        borderLeft: "3px solid var(--primary-color)"
                      }}
                    >
                      {message.reasoningSteps && message.reasoningSteps.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {message.reasoningSteps.map((step, idx) => {
                            const toolCallKey = `${message.id}-${idx}`;
                            const isExpanded = expandedToolCalls[toolCallKey];
                            const contentPreview = step.content.length > 100 ? step.content.substring(0, 100) + "..." : step.content;

                            return (
                              <div key={idx} style={{
                                padding: "6px 8px",
                                backgroundColor: "var(--surface-0)",
                                borderRadius: "var(--border-radius-sm)",
                                fontSize: "var(--font-size-sm)",
                                cursor: "pointer",
                                border: "1px solid var(--content-border-color)",
                                transition: "all 0.2s ease"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--surface-50)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--surface-0)";
                              }}
                              >
                                <div
                                  onClick={() => toggleToolCall(message.id, idx)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "8px",
                                    marginBottom: isExpanded ? "8px" : "0"
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                                    <span style={{ fontSize: "0.75rem", color: "var(--text-muted-color)", flexShrink: 0 }}>
                                      {step.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                    </span>
                                    {step.tool_name && (
                                      <span style={{
                                        fontSize: "0.75rem",
                                        padding: "2px 6px",
                                        backgroundColor: "var(--color-blue-500)",
                                        color: "var(--surface-default)",
                                        borderRadius: "4px",
                                        fontWeight: "var(--font-weight-600)",
                                        flexShrink: 0
                                      }}>
                                        {step.tool_name}
                                      </span>
                                    )}
                                    {!isExpanded && (
                                      <span style={{
                                        fontSize: "0.75rem",
                                        color: "var(--text-muted-color)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                      }}>
                                        {contentPreview.replace(/[#*`\n]/g, ' ').trim()}
                                      </span>
                                    )}
                                  </div>
                                  <i
                                    className={`pi ${isExpanded ? "pi-chevron-up" : "pi-chevron-down"}`}
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "var(--text-muted-color)",
                                      transition: "transform 0.2s ease",
                                      flexShrink: 0
                                    }}
                                  ></i>
                                </div>

                                {isExpanded && (
                                  <>
                                    <div style={{
                                      color: "var(--text-color)",
                                      lineHeight: "1.4",
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                      overflowWrap: "break-word",
                                      wordBreak: "break-word"
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    >
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                          pre: ({ children }) => (
                                            <pre style={{
                                              backgroundColor: 'var(--surface-200)',
                                              padding: '0.5rem',
                                              borderRadius: '0.25rem',
                                              overflow: 'auto',
                                              fontSize: '0.75rem',
                                              fontFamily: 'monospace',
                                              margin: '0.5rem 0',
                                              maxWidth: '100%',
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word'
                                            }}>
                                              {children}
                                            </pre>
                                          ),
                                          code: ({ children }) => (
                                            <code style={{
                                              backgroundColor: 'var(--surface-200)',
                                              padding: '0.125rem 0.25rem',
                                              borderRadius: '0.25rem',
                                              fontSize: '0.75rem',
                                              fontFamily: 'monospace',
                                              overflowWrap: 'break-word',
                                              wordBreak: 'break-word'
                                            }}>
                                              {children}
                                            </code>
                                          )
                                        }}
                                      >
                                        {step.content}
                                      </ReactMarkdown>
                                    </div>
                                    {step.tool_result && (
                                      <div style={{
                                        marginTop: "8px",
                                        padding: "8px",
                                        backgroundColor: "var(--surface-100)",
                                        borderRadius: "4px",
                                        fontSize: "0.75rem",
                                        fontFamily: "monospace",
                                        color: "var(--text-muted-color)",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        maxHeight: "400px",
                                        overflowY: "auto",
                                        borderLeft: "3px solid var(--color-blue-500)"
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      >
                                        <div style={{
                                          fontSize: "0.7rem",
                                          fontWeight: "var(--font-weight-600)",
                                          marginBottom: "4px",
                                          color: "var(--color-blue-500)",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.05em"
                                        }}>
                                          Tool Output
                                        </div>
                                        {step.tool_result}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className='my-0' style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-muted-color)',
                          fontStyle: 'italic'
                        }}>
                          No reasoning steps recorded for this response.
                        </p>
                      )}
                    </div>
                  )}

                  {/* References Accordion Content */}
                  {openReference[message.id] && (
                    <div
                      style={{
                        marginTop: "4px",
                        padding: "8px 12px",
                        backgroundColor: "var(--surface-50)",
                        borderRadius: "var(--border-radius-md)",
                        borderLeft: "3px solid var(--primary-color)"
                      }}
                    >
                      {message.references && message.references.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {message.references.map((reference, idx) => {
                            const isSelected = selectedReference?.reference_id === reference.reference_id;

                            // Add page number to label if it exists
                            const chipLabel = reference.page_number
                              ? `${reference.title} (p.${reference.page_number})`
                              : reference.title;

                            return (
                              <Chip
                                key={`${message.id}-${reference.reference_id ?? idx}`}
                                label={chipLabel}
                                icon={`pi ${getReferenceIcon(reference.reference_type)}`}
                                onClick={() => toggleReference(message.id, reference.reference_id)}
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? 'var(--color-blue-500)' : 'var(--surface-100)',
                                  color: isSelected ? 'var(--surface-default)' : 'var(--text-color)',
                                  border: isSelected ? '2px solid var(--color-blue-500)' : '1px solid var(--content-border-color)',
                                  transition: 'all 0.2s ease'
                                }}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <p className='my-0' style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-muted-color)',
                          fontStyle: 'italic'
                        }}>
                          No references available.
                        </p>
                      )}
                    </div>
                  )}

                  </div>
                )}
              </div>
            </div>
          );
          })}
          <div ref={messagesEndRef} />
        </div>

      </div>

      {/* Suggested Questions */}
      {getSuggestedQuestions().length > 0 && !isLoading && (
        <div style={{
          borderTop: '1px solid var(--content-border-color)',
          padding: 'var(--padding-sm) var(--padding-md)',
          backgroundColor: 'var(--surface-50)'
        }}>
          <button
            type="button"
            onClick={() => setSuggestionsOpen((prev) => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted-color)',
              fontWeight: 'var(--font-weight-500)'
            }}
            aria-expanded={suggestionsOpen}
          >
            Suggested questions
            {suggestionsOpen ? <ChevronUpIcon /> : <ChevronDown />}
          </button>
          <div
            style={{
              overflow: 'hidden',
              maxHeight: suggestionsOpen ? '400px' : '0px',
              opacity: suggestionsOpen ? 1 : 0,
              transition: 'max-height 0.25s ease, opacity 0.2s ease',
            }}
          >
            <div className="flex flex-wrap" style={{ gap: 'var(--gap-xs)', marginTop: 'var(--gap-xs)' }}>
              {getSuggestedQuestions().map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestionClick(question)}
                  className="transition-colors"
                  style={{
                    padding: 'var(--padding-xs) var(--padding-sm)',
                    backgroundColor: 'var(--surface-0)',
                    border: '1px solid var(--form-field-border-color)',
                    borderRadius: 'var(--border-radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-color)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = coachingButton === "assistant" ? 'var(--color-blue-500)' : 'var(--color-amber-700)' ;
                    e.currentTarget.style.color = 'var(--primary-contrast-color)';
                  // e.currentTarget.style.borderColor = 'var(--color-blue-500)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--surface-0)';
                    e.currentTarget.style.color = 'var(--text-color)';
                  // e.currentTarget.style.borderColor = 'var(--form-field-border-color)';
                  }}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div
        style={{
          borderTop: '1px solid var(--content-border-color)',
          padding: 'var(--padding-md)',
        }}
      >
        <form
          onSubmit={handleSubmit}
          className="flex items-center"
          style={{
            gap: 'var(--gap-sm)',
          }}
        >
          {/* LEFT: upload icon */}
          <button
            type="button"
            onClick={handleFileUpload}
            className="transition-colors flex items-center justify-center"
            disabled={uploadStatus.loading}
            title={ 'Upload file'
            }
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '999px',
              background: 'transparent',
              border: 'none',
              cursor:
                uploadStatus.loading  ? 'not-allowed' : 'pointer',
              color:
                uploadStatus.loading
                  ? 'var(--form-field-disabled-color)'
                  : 'var(--text-muted-color)',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              if (!uploadStatus.loading) {
                e.currentTarget.style.color = 'var(--text-hover-color)';
              }
            }}
            onMouseLeave={(e) => {
              if (!uploadStatus.loading) {
                e.currentTarget.style.color = 'var(--text-muted-color)';
              }
            }}
          >
            <UploadIcon />
            {uploadStatus.loading && (
              <span className="absolute -top-1 -right-1 w-2 h-2">
                <span
                  className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ backgroundColor: 'var(--color-blue-400)' }}
                />
                <span
                  className="relative inline-flex rounded-full h-2 w-2"
                  style={{ backgroundColor: 'var(--color-blue-500)' }}
                />
              </span>
            )}
          </button>

          {/* hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept=".pdf,.docx,.txt,.md,.ppt,.pptx,.xlsx,.csv,.msg"
          />

          {/* CENTER: message input (matches Figma big rounded area) */}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1"
            disabled={uploadStatus.loading}
            style={{
              padding: '18px 20px',
              border: '1px solid var(--form-field-border-color)',
              borderRadius: '10px',
              backgroundColor: uploadStatus.loading
                ? 'var(--form-field-disabled-background)'
                : 'var(--form-field-background)',
              color: uploadStatus.loading
                ? 'var(--form-field-disabled-color)'
                : 'var(--form-field-color)',
              fontSize: 'var(--typography-body-font-size)',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(18, 18, 23, 0.05)',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor =
                'var(--form-field-focus-border-color)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor =
                'var(--form-field-border-color)';
            }}
            onMouseEnter={(e) => {
              if (!isLoading && !uploadStatus.loading) {
                e.currentTarget.style.borderColor =
                  'var(--form-field-hover-border-color)';
              }
            }}
            onMouseLeave={(e) => {
              if (
                !isLoading &&
                !uploadStatus.loading &&
                e.currentTarget !== document.activeElement
              ) {
                e.currentTarget.style.borderColor =
                  'var(--form-field-border-color)';
              }
            }}
          />

          {/* RIGHT: arrow icon, visually like Figma */}
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading || uploadStatus.loading}
            className="transition-colors flex items-center justify-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '999px',
              border: 'none',
              background: 'transparent',
              cursor:
                !inputValue.trim() || isLoading || uploadStatus.loading
                  ? 'not-allowed'
                  : 'pointer',
              color:
                !inputValue.trim() || isLoading || uploadStatus.loading
                  ? 'var(--form-field-disabled-color)'
                  : 'var(--color-blue-500)',
            }}
            onMouseEnter={(e) => {
              if (inputValue.trim() && !isLoading && !uploadStatus.loading) {
                e.currentTarget.style.color = 'var(--primary-hover-color)';
              }
            }}
            onMouseLeave={(e) => {
              if (inputValue.trim() && !isLoading && !uploadStatus.loading) {
                e.currentTarget.style.color = 'var(--color-blue-500)';
              }
            }}
          >
            <SendIcon />
          </button>
        </form>

        {uploadStatus.error && (
          <div
            style={{
              marginTop: 'var(--gap-xs)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--status-error)',
            }}
          >
            {uploadStatus.error}
          </div>
        )}

        {/* TOGGLE centered under input (AI Assistant / Negotiation Coach) */}
        <div
          className="coachin-button"
          style={{
            marginTop: 'var(--gap-sm)',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <SelectButton
            value={coachingButton}
            onChange={(e) => {
              if(!e.value) return
              createNewChat();
              setCoachingButton(e.value)
            }}
            options={options}
            optionLabel="label"
            itemTemplate={(option) => (
              <div className="toggle-item flex items-center gap-2">
                {option.icon}
                <span>{option.label}</span>
              </div>
            )}
            className="mode-toggle pointer"
          />
        </div>
      </div>

      {/* Feedback Modal */}
      <Dialog
        header="Provide Feedback"
        visible={feedbackModal}
        onHide={handleFeedbackCancel}
        modal
        className="w-[400px]"
        appendTo="self" // keep dialog inside widget shadow DOM so styles travel with it
        footer={
          <div className="flex justify-end gap-2">
            {/* <button
              onClick={handleFeedbackCancel}
            >Cancel</button>
            <button
              onClick={handleFeedbackSubmit}
              disabled={!Object.values(feedbackOptions).some(option => option) && !feedbackText.trim()}
            >Submit</button> */}
            <CustomButton
              iconsOnly={false}
              variant='secondary'
              label="Cancel"
              onClick={handleFeedbackCancel}
            />

            <CustomButton
              iconsOnly={false}
              variant='primary'
              label="Submit"
              disabled={!Object.values(feedbackOptions).some(option => option) && !feedbackText.trim()}
              onClick={handleFeedbackSubmit}
            />
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            We're sorry this response wasn't helpful. Please tell us what went wrong so we can improve.
          </p>
          
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">What was the issue?</p>
            
            <div className="flex items-center">
              <Checkbox
                inputId="factsWithoutNarrative"
                checked={feedbackOptions.factsWithoutNarrative}
                onChange={() => handleCheckboxChange('factsWithoutNarrative')}
              />
              <label htmlFor="factsWithoutNarrative" className="ml-2 text-sm text-gray-700">
                Facts listed without being turned into a clear narrative.
              </label>
            </div>
            
            <div className="flex items-center">
              <Checkbox
                inputId="weakCommercialLink"
                checked={feedbackOptions.weakCommercialLink}
                onChange={() => handleCheckboxChange('weakCommercialLink')}
              />
              <label htmlFor="weakCommercialLink" className="ml-2 text-sm text-gray-700">
                Weak link to core commercial or contextual drivers.
              </label>
            </div>
            
            <div className="flex items-center">
              <Checkbox
                inputId="risksNotAcknowledged"
                checked={feedbackOptions.risksNotAcknowledged}
                onChange={() => handleCheckboxChange('risksNotAcknowledged')}
              />
              <label htmlFor="risksNotAcknowledged" className="ml-2 text-sm text-gray-700">
                Risks not acknowledged.
              </label>
            </div>
            
            <div className="flex items-center">
              <Checkbox
                inputId="incorrectSource"
                checked={feedbackOptions.incorrectSource}
                onChange={() => handleCheckboxChange('incorrectSource')}
              />
              <label htmlFor="incorrectSource" className="ml-2 text-sm text-gray-700">
                Source information was incorrect.
              </label>
            </div>
            
            <div className="flex items-center">
              <Checkbox
                inputId="incorrectAnswer"
                checked={feedbackOptions.incorrectAnswer}
                onChange={() => handleCheckboxChange('incorrectAnswer')}
              />
              <label htmlFor="incorrectAnswer" className="ml-2 text-sm text-gray-700">
                Overall answer was incorrect.
              </label>
            </div>
          </div>
          
          <div>
            <label htmlFor="additionalFeedback" className="block text-sm font-medium text-gray-700 mb-2">
              Additional feedback (optional):
            </label>
            <InputTextarea
              id="additionalFeedback"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={3}
              placeholder="Please provide any additional details..."
              className="feedback-textarea w-full"
            />
          </div>
        </div>
      </Dialog>
      
      {/* Citation Panel */}
      
        {/* History Panel - Slides in from the right */}
        {threads.length > 0 && isHistoryPanelOpen && (
          <div className="absolute top-0 right-0 h-full z-10 overflow-y-auto" style={{
            width: '16rem',
            backgroundColor: 'var(--surface-0)',
            borderLeft: '1px solid var(--content-border-color)',
            boxShadow: 'var(--overlay-popover-shadow)'
          }}>
          <div className="flex items-center justify-between" style={{
            padding: 'var(--padding-md)',
            borderBottom: '1px solid var(--content-border-color)'
          }}>
            <h4 style={{
              fontSize: 'var(--typography-heading-sm-font-size)',
              fontWeight: 'var(--font-weight-700)',
              color: 'var(--text-color)',
              margin: 0
            }}>Chat History</h4>
            <button
              onClick={toggleHistoryPanel}
              className="transition-colors"
              type="button"
              style={{
                color: 'var(--text-muted-color)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 'var(--padding-xs)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-hover-color)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted-color)'}
            >
             <Cancel />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto" style={{ padding: 'var(--padding-sm)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-xs)' }}>
              {threads.map((thread) => (
                <button
                  key={thread.id}
                  onClick={() => {
                    loadThread(thread.id);
                    toggleHistoryPanel(); // Close panel after selecting
                  }}
                  className="w-full text-left transition-colors truncate block"
                  title={thread.title}
                  style={{
                    padding: 'var(--list-option-padding)',
                    borderRadius: 'var(--list-option-border-radius)',
                    border: '1px solid',
                    backgroundColor: currentThreadId === thread.id ? 'var(--list-option-selected-background)' : 'transparent',
                    borderColor: currentThreadId === thread.id ? 'var(--primary-color)' : 'transparent',
                    color: currentThreadId === thread.id ? 'var(--list-option-selected-color)' : 'var(--list-option-color)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (currentThreadId !== thread.id) {
                      e.currentTarget.style.backgroundColor = 'var(--list-option-focus-background)';
                      e.currentTarget.style.color = 'var(--list-option-focus-color)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentThreadId !== thread.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--list-option-color)';
                    }
                  }}
                >
                  <div className="truncate" style={{
                    fontWeight: 'var(--font-weight-500)',
                    fontSize: 'var(--typography-body-font-size)'
                  }}>{thread.title}</div>
                  <div className="mt-1" style={{
                    color: 'var(--text-muted-color)',
                    fontSize: 'var(--font-size-xs)'
                  }}>
                    {new Date(thread.created_at).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};
